import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastModule } from 'primeng/toast';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { debounceTime, distinctUntilChanged, Subject, Subscription } from 'rxjs';
import { InputTextModule } from 'primeng/inputtext';
import { DatePickerModule } from 'primeng/datepicker';
import { PaginatorModule } from 'primeng/paginator';
import { DialogModule } from 'primeng/dialog';
import { TabViewModule } from 'primeng/tabview';
import { TooltipModule } from 'primeng/tooltip';
import { ServiceOrderService } from '../../../services/service-order.service';
import { ServiceOrder } from '../../../models/service-order.model';
import { ServiceOrderChecklistItem } from '../../../models/service-order-checklist.model';
import { CustomFieldValue, CustomFieldDefinition } from '../../../models/custom-field.model';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-list-service-order',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ConfirmDialogModule,
    ToastModule,
    TableModule,
    ButtonModule,
    DropdownModule,
    InputTextModule,
    DatePickerModule,
    PaginatorModule,
    DialogModule,
    TabViewModule,
    TooltipModule
  ],
  providers: [ConfirmationService, MessageService],
  templateUrl: './list-service-order.component.html'
})
export class ListServiceOrderComponent implements OnInit {

  status = [
    { label: 'Todos', value: null},
    { label: 'Pendente', value: 'PENDING' },
    { label: 'Concluída', value: 'COMPLETED' },
    { label: 'Cancelada', value: 'CANCELLED' }
  ];

  private searchSubject = new Subject<string>();
  private searchSubscription?: Subscription;

  currentDate = new Date();

  searchTerm: string = '';
  initialDate: Date = this.currentDate;
  finalDate: Date = this.currentDate;
  selectedStatus = this.status[0].value;
  serviceOrders: ServiceOrder[] = [];
  loading: boolean = false;
  totalRecords: number = 0;

  first: number = 0;
  rows: number = 10;
  currentPage: number = 1;
  
  // Modal de visualização
  displayModal: boolean = false;
  selectedServiceOrder: ServiceOrder | null = null;
  checklistItems: ServiceOrderChecklistItem[] = [];
  customFieldValues: CustomFieldValue[] = [];
  customFieldDefinitions: CustomFieldDefinition[] = [];
  loadingModal: boolean = false;
  
  constructor(
    private serviceOrderService: ServiceOrderService,
    private router: Router,
    private confirmationService: ConfirmationService,
    private messageService: MessageService
  ) {
    this.searchSubscription = this.searchSubject.pipe(
          debounceTime(300),
          distinctUntilChanged()
        ).subscribe(term => {
          this.searchTerm = term;
          this.getData();
        });
  }

  ngOnInit(): void {
    this.getData();
  }

  getData(){
    this.loading = true;
    this.serviceOrderService.getAll(this.searchTerm, this.initialDate, this.finalDate, this.selectedStatus, this.currentPage).subscribe({
        next: (data) => {
          this.serviceOrders = data.data;
          this.totalRecords = data.pagination?.totalRecords || 0;
          this.loading = false;
        },
        error: (error) => {
          this.loading = false;
        }
      })
  }


  onSearchChange() 
  {
    this.searchSubject.next(this.searchTerm);
  }

  onStatusChange(){
    this.getData();
  }

  onPageChange(event: any): void {
    this.first = event.first;
    this.currentPage = Math.floor(this.first / this.rows) + 1;
    this.getData();
  }

  createServiceOrder(){
    this.router.navigate(['/app/service-orders/new']);
  }

  view(serviceOrder: ServiceOrder){
      this.router.navigate(['/app/service-orders/edit', serviceOrder.id]);
  }

  openViewModal(serviceOrder: ServiceOrder) {
    this.selectedServiceOrder = serviceOrder;
    this.displayModal = true;
    this.loadingModal = true;
    
    const companyId = 1; // TODO: Obter do contexto/auth
    
    // Carregar dados completos da OS
    forkJoin({
      order: this.serviceOrderService.getById(serviceOrder.id),
      checklist: this.serviceOrderService.getChecklistItems(serviceOrder.id),
      customFields: this.serviceOrderService.getCustomFieldValues(serviceOrder.id),
      fieldDefinitions: this.serviceOrderService.getCustomFieldDefinitions(undefined, companyId)
    }).subscribe({
      next: (results) => {
        // Atualizar dados da OS
        if (results.order?.data) {
          this.selectedServiceOrder = results.order.data;
        }
        
        // Carregar checklist
        if (results.checklist?.data) {
          this.checklistItems = results.checklist.data.sort((a, b) => a.order - b.order);
        } else {
          this.checklistItems = [];
        }
        
        // Carregar campos personalizados
        if (results.customFields?.data) {
          this.customFieldValues = results.customFields.data;
        } else {
          this.customFieldValues = [];
        }
        
        // Carregar definições dos campos para obter labels
        if (results.fieldDefinitions?.data) {
          this.customFieldDefinitions = results.fieldDefinitions.data;
        } else {
          this.customFieldDefinitions = [];
        }
        
        this.loadingModal = false;
      },
      error: (err) => {
        console.error('Erro ao carregar dados da OS:', err);
        this.messageService.add({ 
          severity: 'error', 
          summary: 'Erro!', 
          detail: 'Não foi possível carregar os dados da OS', 
          life: 3000 
        });
        this.loadingModal = false;
      }
    });
  }

  closeModal() {
    this.displayModal = false;
    this.selectedServiceOrder = null;
    this.checklistItems = [];
    this.customFieldValues = [];
    this.customFieldDefinitions = [];
  }

  getFieldLabel(fieldValue: CustomFieldValue): string {
    // Buscar definição do campo pelo ID para obter o label correto
    const definition = this.customFieldDefinitions.find(
      def => def.id === fieldValue.customFieldDefinitionId
    );
    
    // Se encontrar a definição, usar o label, senão usar o fieldName como fallback
    return definition?.label || fieldValue.fieldName;
  }

  getStatusLabel(status: string): string {
    switch(status) {
      case 'PENDING': return 'Pendente';
      case 'COMPLETED': return 'Concluída';
      case 'CANCELLED': return 'Cancelada';
      default: return status;
    }
  }

  getStatusClass(status: string): string {
    switch(status) {
      case 'PENDING': return 'bg-yellow-100 text-yellow-800';
      case 'COMPLETED': return 'bg-green-100 text-green-800';
      case 'CANCELLED': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  }

  cancel(serviceOrder: ServiceOrder){
    this.confirmationService.confirm({
      message: 'Tem certeza que deseja cancelar esta Ordem de Serviço?',
      header: 'Confirmação',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sim',
      rejectLabel: 'Não',
      accept: () => {
        this.serviceOrderService.cancel(serviceOrder.id).subscribe({
          next: () => {
            this.messageService.add({ severity: 'success', summary: 'Sucesso!', detail: 'OS cancelada com sucesso!', life: 3000 });
            this.getData();
          },
          error: (error) => {
            this.messageService.add({ severity: 'error', summary: 'Erro!', detail: 'Não foi possível cancelar a OS!', life: 3000 });
          }
        });
      }
    });
  }
}

